// React owns everything document-like (A4). Boot loading screen, title, shell bar, screens by
// RouteStore, the overlays (system menu, settings, level-up, confirm dialogs, outcome curtain, the
// connection notices of docs/54) and toasts.
import { useEffect } from 'react';
import { useRoute } from '../core/stores/route';
import { useSession } from '../core/stores/session';
import { EncounterHud } from './encounter/EncounterHud';
import { WorldScreen } from './screens/World';
import { VillageScreen } from './screens/Village';
import { LobbyScreen } from './screens/Lobby';
import { AftermathScreen } from './screens/Aftermath';
import { LocalMapScreen } from './screens/LocalMap';
import { TitleScreen } from './screens/Title';
import { PlayOnline } from './screens/PlayOnline';
import { CollectionScreen } from './screens/Collection';
import { GatheringScreen } from './screens/Gathering';
import { IntroScreen } from './screens/intro/Intro';
import { WritingScreen } from './screens/intro/Writing';
import { EpilogueScreen } from './screens/intro/Epilogue';
import { ChapterScreen } from './screens/intro/Chapter';
import { SettingsModal } from './screens/Settings';
import { SystemMenu } from './screens/SystemMenu';
import { LevelUpModal } from './screens/LevelUp';
import { HeroPathHost } from './heroes/HeroPath';
import { BARLESS, Shell } from './screens/Shell';
import { RouteDriver } from './RouteDriver';
import { HeroLookSync } from '../core/heroes/looks';
import { PathCurtain } from './screens/path/QuestPath';
import { ScreenCurtain } from './ScreenCurtain';
import { BandCalls } from './screens/BandCalls';
import { Banner, Button, Curtain, DialogHost, LoadingScreen, Panel, Toasts, useUiSettings, type LoadingPhase } from './components';
import { useKeyboard } from './useKeyboard';
import { useOverlay } from './overlay';
import { useDialogs } from './components/Modal';
import { BootOffline, LinkNotices } from './screens/Connection';
import { MusicDirector } from '../core/audio/music';
import { EmoteHost } from './emote/EmoteWheel';
import { HeroSheetHost } from './heroes/HeroSheet';
import { InventoryHost } from './inventory/Inventory';
import { SetFanfareHost } from './sets/SetFanfare';
import { isHotkey } from '../core/input/hotkeys';
import { BlessingHost } from './encounter/Blessing';

export const BOOT_PHASES: LoadingPhase[] = [
  { id: 'booting', label: 'Waking the simulator' },
  { id: 'auth', label: 'Signing in' },
  { id: 'connecting', label: 'Reaching the World' },
];

/** Esc opens/closes the system menu on document screens (the encounter keeps Esc for authoring). */
function useSystemMenuKey() {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!isHotkey(e, 'menu')) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT')) return;
      const o = useOverlay.getState();
      if (o.settings || o.levelUp !== undefined || o.credits || useDialogs.getState().queue.length) return; // the Modal handles it
      const screen = useRoute.getState().screen;
      if (screen === 'encounter' && !o.menu) return;
      if (useSession.getState().phase !== 'ready' && useSession.getState().phase !== 'incompatible') return;
      e.preventDefault();
      o.toggleMenu();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
}

/** The outcome curtain (a room's result), shown once and cleared when it has faded. */
function OutcomeHost() {
  const outcome = useOverlay((s) => s.outcome);
  const clear = useOverlay((s) => s.clearOutcome);
  const screen = useRoute((s) => s.screen);
  // The Aftermath and the Spoils screen stamp their own result; don't stack the room's curtain on it.
  const stamped = screen === 'aftermath' || screen === 'spoils';
  useEffect(() => { if (stamped && outcome) clear(); }, [stamped, outcome, clear]);
  if (!outcome || stamped) return null;
  return <Curtain key={outcome.key} tone={outcome.tone} eyebrow={outcome.eyebrow} word={outcome.word} sub={outcome.sub} onDone={clear} />;
}

export function App() {
  const screen = useRoute((s) => s.screen);
  const phase = useSession((s) => s.phase);
  const error = useSession((s) => s.error);
  useUiSettings();
  useKeyboard();
  useSystemMenuKey();
  if (phase === 'offline') return <BootOffline />;
  if (phase === 'booting' || phase === 'auth' || phase === 'connecting') {
    return <LoadingScreen phases={BOOT_PHASES} current={phase} />;
  }
  const overlays = <><HeroLookSync /><SystemMenu /><SettingsModal /><LevelUpModal /><HeroPathHost /><DialogHost /><Toasts placement={screen === 'encounter' || screen === 'spoils' ? 'board' : 'corner'} /><OutcomeHost /><LinkNotices /><EmoteHost /><HeroSheetHost /><InventoryHost /><SetFanfareHost /><PathCurtain /><ScreenCurtain /><BandCalls /><BlessingHost /></>;
  if (screen === 'title') return <><RouteDriver /><MusicDirector /><TitleScreen />{overlays}</>;
  return (
    <>
      <RouteDriver />
      <MusicDirector />
      {!BARLESS.includes(screen) && <Shell />}
      {phase === 'incompatible' && <Banner tone="warn" mono data-testid="incompatible">Client incompatible with the module ({error}). The World and Chronicle stay readable; planning is blocked. Reload after updating.</Banner>}
      {screen === 'online' && <PlayOnline />}
      {/* docs/131: the collection log, from the main menu */}
      {screen === 'collection' && <CollectionScreen />}
      {screen === 'gathering' && <GatheringScreen />}
      {screen === 'writing' && <WritingScreen />}
      {screen === 'intro' && <IntroScreen />}
      {screen === 'epilogue' && <EpilogueScreen />}
      {screen === 'chapter' && <ChapterScreen />}
      {screen === 'world' && <WorldScreen />}
      {screen === 'village' && <VillageScreen />}
      {screen === 'lobby' && <LobbyScreen />}
      {screen === 'localmap' && <LocalMapScreen />}
      {/* docs/84: the Spoils go up over the finished encounter (the same HUD, kept mounted) */}
      {(screen === 'encounter' || screen === 'spoils') && <EncounterHud spoils={screen === 'spoils'} />}
      {screen === 'aftermath' && <AftermathScreen />}
      {!['online', 'collection', 'gathering', 'writing', 'intro', 'epilogue', 'chapter', 'world', 'village', 'lobby', 'localmap', 'encounter', 'spoils', 'aftermath'].includes(screen) && <div className="fk-center"><Panel className="fk-hit" title={screen}><p className="fk-muted">This screen is not built yet.</p><Button onClick={() => useRoute.getState().go('world')}>Back to the World</Button></Panel></div>}
      {overlays}
    </>
  );
}
